/* eslint-disable @typescript-eslint/no-explicit-any -- Generic mock data store requires any for dynamic collections */
/**
 * Global demo mode manager
 * Singleton that tracks demo mode state and provides mock data access
 */

import { IS_STATIC_DEMO } from './config';

const STORAGE_KEY = 'aivilo_lens_demo_data';
const STORAGE_VERSION = '5.1';

export interface MockDataStore {
  isReady: boolean;
  store: Record<string, any> | null;
  get(collection: string): any;
  getById(collection: string, id: string): any;
  create(collection: string, item: Record<string, any>): Record<string, any> | null;
  update(collection: string, id: string, updates: Record<string, any>): Record<string, any> | null;
  remove(collection: string, id: string): boolean;
  query(collection: string, predicate: (item: any) => boolean): any[];
  count(collection: string, predicate?: (item: any) => boolean): number;
  clearStore(): void;
  resetStore(): void;
  updateStore(updater: ((data: Record<string, any>) => Record<string, any>) | Record<string, any>): void;
}

export interface DemoModeState {
  isDemoMode: boolean;
  mode: string;
  mockDataStore: MockDataStore | null;
}

export type DemoModeListener = (state: DemoModeState) => void;

/**
 * Create an eager mock data store for IS_STATIC_DEMO mode.
 * This runs synchronously at module load time so the store is
 * available before any React component mounts (avoids race conditions).
 */
function createEagerMockDataStore(): MockDataStore {
  // Dynamic import would be async; use require-style import at top of module
  // eslint-disable-next-line global-require
  const mockData = require('./mockData').default;

  let data: Record<string, any> | null = null;

  // Try loading from localStorage first
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.version === STORAGE_VERSION) {
        data = parsed.data;
      }
    }
  } catch (e) { console.error('Failed to load demo store from localStorage', e); }

  if (!data) {
    data = JSON.parse(JSON.stringify(mockData));
  }

  const saveToStorage = (d: Record<string, any>) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: STORAGE_VERSION,
        data: d,
        timestamp: new Date().toISOString()
      }));
    } catch (e) { console.error('Failed to save demo store to localStorage', e); }
  };

  return {
    isReady: true,
    store: data as Record<string, any>,

    get(collection: string) {
      return (data as Record<string, any>)[collection] || [];
    },

    getById(collection: string, id: string) {
      if (!(data as Record<string, any>)[collection]) return null;
      const items = Array.isArray((data as Record<string, any>)[collection])
        ? (data as Record<string, any>)[collection]
        : [(data as Record<string, any>)[collection]];
      return items.find((item: Record<string, unknown>) => item.id === id) || null;
    },

    create(collection: string, item: Record<string, any>) {
      const newItem = {
        ...item,
        id: item.id || crypto.randomUUID(),
        created_at: item.created_at || new Date().toISOString(),
        updated_at: item.updated_at || new Date().toISOString()
      };
      if (Array.isArray((data as Record<string, any>)[collection])) {
        (data as Record<string, any>)[collection] = [...(data as Record<string, any>)[collection], newItem];
      } else {
        (data as Record<string, any>)[collection] = newItem;
      }
      saveToStorage(data as Record<string, any>);
      return newItem;
    },

    update(collection: string, id: string, updates: Record<string, any>) {
      if (!(data as Record<string, any>)[collection]) return null;
      const items = Array.isArray((data as Record<string, any>)[collection])
        ? (data as Record<string, any>)[collection]
        : [(data as Record<string, any>)[collection]];
      const index = items.findIndex((item: Record<string, unknown>) => item.id === id);
      if (index === -1) return null;
      const updatedItem = { ...items[index], ...updates, id, updated_at: new Date().toISOString() };
      const newItems = [...items];
      newItems[index] = updatedItem;
      (data as Record<string, any>)[collection] = Array.isArray((data as Record<string, any>)[collection]) ? newItems : updatedItem;
      saveToStorage(data as Record<string, any>);
      return updatedItem;
    },

    remove(collection: string, id: string) {
      if (!(data as Record<string, any>)[collection]) return false;
      const items = Array.isArray((data as Record<string, any>)[collection])
        ? (data as Record<string, any>)[collection]
        : [(data as Record<string, any>)[collection]];
      const index = items.findIndex((item: Record<string, unknown>) => item.id === id);
      if (index === -1) return false;
      (data as Record<string, any>)[collection] = items.filter((item: Record<string, unknown>) => item.id !== id);
      saveToStorage(data as Record<string, any>);
      return true;
    },

    query(collection: string, predicate: (item: any) => boolean) {
      if (!(data as Record<string, any>)[collection]) return [];
      const items = Array.isArray((data as Record<string, any>)[collection])
        ? (data as Record<string, any>)[collection]
        : [(data as Record<string, any>)[collection]];
      return items.filter(predicate);
    },

    count(collection: string, predicate?: (item: any) => boolean) {
      if (!(data as Record<string, any>)[collection]) return 0;
      const items = Array.isArray((data as Record<string, any>)[collection])
        ? (data as Record<string, any>)[collection]
        : [(data as Record<string, any>)[collection]];
      return predicate ? items.filter(predicate).length : items.length;
    },

    clearStore() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { console.error('Failed to clear demo store', e); }
      data = null;
    },

    resetStore() {
      data = JSON.parse(JSON.stringify(mockData));
      saveToStorage(data as Record<string, any>);
    },

    updateStore(updater: ((d: Record<string, any>) => Record<string, any>) | Record<string, any>) {
      data = typeof updater === 'function' ? updater(data as Record<string, any>) : updater;
      saveToStorage(data as Record<string, any>);
    }
  };
}

class DemoModeManager {
  isDemoMode: boolean;
  mode: string;
  mockDataStore: MockDataStore | null;
  private listeners: DemoModeListener[];

  constructor() {
    // In static demo mode, always force isDemoMode to true
    this.isDemoMode = IS_STATIC_DEMO ? true : false;
    this.mode = 'subscribed';
    // Eagerly initialize mock store in static demo mode so it's ready
    // before any React component mounts (prevents race conditions)
    this.mockDataStore = IS_STATIC_DEMO ? createEagerMockDataStore() : null;
    this.listeners = [];
  }

  setDemoMode(isDemoMode: boolean, mode: string, mockDataStore: MockDataStore | null): void {
    // In static demo mode, never allow turning off demo mode
    this.isDemoMode = IS_STATIC_DEMO ? true : isDemoMode;
    this.mode = mode;
    // In static demo, keep the eager store if the React store isn't ready yet
    if (!IS_STATIC_DEMO || mockDataStore?.isReady) {
      this.mockDataStore = mockDataStore;
    }
    this.notifyListeners();
  }

  getDemoMode(): DemoModeState {
    return {
      isDemoMode: this.isDemoMode,
      mode: this.mode,
      mockDataStore: this.mockDataStore
    };
  }

  subscribe(listener: DemoModeListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.getDemoMode()));
  }

  clearMockData(): void {
    if (this.mockDataStore?.clearStore) {
      this.mockDataStore.clearStore();
    }
  }
}

// Export singleton instance
export const demoModeManager = new DemoModeManager();

export default demoModeManager;
